import type {ComponentType, Dispatch, SetStateAction} from 'react';

import {ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';

import type {Extension} from '../content-store/extensionConstants';
import {buildMcpInstallRequestBody, type McpExtensionInstallContext} from './mcp/mcpInstallContext';
import {getMcpInstallWizardSteps} from './mcp/mcpInstallWizardSteps';

export type {McpExtensionInstallContext};

/** Union grows with new wizards; today only MCP defines an install context. */
export type ExtensionInstallContext = McpExtensionInstallContext;

export function createEmptyExtensionInstallContext(extension: Extension): ExtensionInstallContext {
  if (extension.extensionType === ExtensionType.MCP_SERVER) {
    return {
      extensionType: ExtensionType.MCP_SERVER,
      urlParamValues: {},
      manualOAuthClientId: '',
      manualOAuthClientSecret: '',
    };
  }
  throw new Error(
    `createEmptyExtensionInstallContext: unsupported extension type ${String(extension.extensionType)}`
  );
}

export interface ExtensionInstallStepProps {
  extension: Extension;
  installContext: ExtensionInstallContext;
  setInstallContext: Dispatch<SetStateAction<ExtensionInstallContext>>;
}

export interface ExtensionInstallWizardStep {
  id: string;
  label: string;
  stepDescription?: string;
  canProceed: (installContext: ExtensionInstallContext, extension: Extension) => boolean;
  StepBody: ComponentType<ExtensionInstallStepProps>;
}

export function getExtensionInstallWizardSteps(extension: Extension): Array<ExtensionInstallWizardStep> {
  switch (extension.extensionType) {
    case ExtensionType.MCP_SERVER:
      return getMcpInstallWizardSteps(extension);
    default:
      return [];
  }
}

export function buildExtensionInstallRequestBody(
  extension: Extension,
  installContext: ExtensionInstallContext
): Record<string, unknown> {
  switch (extension.extensionType) {
    case ExtensionType.MCP_SERVER:
      return buildMcpInstallRequestBody(extension, installContext);
    default:
      return {extensionType: extension.extensionType};
  }
}

/** Whether installing this extension should open the install wizard instead of a one-shot POST. */
export function extensionNeedsInstallWizard(extension: Extension): boolean {
  return getExtensionInstallWizardSteps(extension).length > 0;
}
